import { useLayoutEffect, useRef } from 'react';
import type { StudioDocument } from './studio-api';
import { StudioIcon } from './StudioIcon';
import { record, text, title } from './studio-content';
import { uiText } from '../../i18n';
import { FadeImg } from '../../ui/LateFade';
import { reduced } from '../../ui/motion';
import { type TrendFrame, trendPreview } from './studio-trend-preview';

/** Бейдж строки: сервер отдаёт ключ (§6.7), словами его называет клиент — как в строках инструментов */
const BADGES: Record<string, string> = { hit: 'Хит', new: 'Новое' };

/** Кадр превью шаблона — плитка коллажа 40×48, как портрет: картинка или GIF — FadeImg (скелетон плитки, пока едет, затем проявление);
    ролик — без звука по кругу, в заморозке .still и при «уменьшить движение» стоит первым кадром */
export function TrendFrame({ url, video }: TrendFrame) {
  if (!video) return <FadeImg src={url} alt="" />;
  return (
    <video
      src={url}
      muted
      loop
      playsInline
      autoPlay={!reduced()}
      preload="metadata"
      aria-hidden="true"
      onLoadedData={event => event.currentTarget.setAttribute('data-ld', '')}
    />
  );
}

/* Подгонка строк тренда — пачкой: все строки сначала пишут число плиток, потом читают, помещается ли название, и так по шагам. Раньше каждая
   строка в своём useLayoutEffect писала и тут же читала (scrollWidth) — браузер раскладывал главную «Студии» заново на каждую строку и шаг
   (замер на ПК: ~60 мс принудительной раскладки на вход в «Студию»). Пачка разбирается микрозадачей — до отрисовки кадра, как и прежде. */
const FIT = new Map<HTMLElement, number>();
let fitQueued = false;
function fitLater(row: HTMLElement, count: number) {
  FIT.set(row, count);
  if (fitQueued) return;
  fitQueued = true;
  queueMicrotask(fitAll);
}
function fitAll() {
  fitQueued = false;
  const put = (row: HTMLElement, shown: number) => {
    row.dataset.k = String(shown);
    row.style.setProperty('--k', String(shown));
  };
  /* чтение: ширины строк */
  const rows = [...FIT].filter(([row]) => row.isConnected && row.getClientRects().length);
  FIT.clear();
  const L = rows.map(([row, count]) => {
    const width = row.clientWidth;
    return {
      row,
      shown: Math.min(count, width < 372 ? 3 : width < 440 ? 4 : 5),
      title: row.querySelector<HTMLElement>('.tt'),
      sub: row.querySelector<HTMLElement>('.m>span'),
    };
  });
  /* запись: начальное число плиток, бейдж на месте */
  L.forEach(x => {
    put(x.row, x.shown);
    x.row.classList.remove('nob');
  });
  const over = (x: (typeof L)[number]) => !!x.title && x.title.scrollWidth > x.title.clientWidth;
  /* шаги: читаем все строки, потом пишем всем — одна раскладка на шаг, а не на строку */
  let left = L;
  while (left.length) {
    const fits = left.map(x => !(x.shown > 2 && (over(x) || (!!x.sub && x.sub.scrollWidth > x.sub.clientWidth))));
    left = left.filter((_, i) => !fits[i]);
    left.forEach(x => put(x.row, --x.shown));
  }
  // Названию приоритет: не помещается и с двумя портретами — убираем бейдж «Хит» / «Новое»
  const nob = L.map(x => over(x) && !!x.row.querySelector('.st-nw'));
  L.forEach((x, i) => {
    if (nob[i]) x.row.classList.add('nob');
  });
}

/** Коллаж уступает место названию и подписи; тот же fitTrend из шаблона.
    count — сколько портретов будет в коллаже (по данным, пока картинки едут), ready — портреты пришли:
    место коллажа и обрезка названия стоят с первого кадра: пока портреты едут — плитки-заглушки (скелетон), портреты проявляются поверх. */
export function StudioTrendRow({
  item,
  tool,
  photos,
  count: photoCount = photos.length,
  ready = true,
  onAction,
}: {
  item: Record<string, unknown>;
  tool?: StudioDocument;
  photos: string[];
  count?: number;
  ready?: boolean;
  onAction(action: unknown): void;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const photoKey = photos.join('|');
  /* кадры превью шаблона вместо портретов: те же плитки коллажа, по кадру в плитке; ждать портреты не нужно */
  const frames = trendPreview(item, tool);
  const count = frames.length || photoCount;
  useLayoutEffect(() => {
    const row = ref.current;
    if (!row) return;
    const fit = () => fitLater(row, count);
    fit();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit);
    observer?.observe(row);
    let alive = true;
    document.fonts?.ready.then(() => {
      if (alive) fit();
    });
    return () => {
      alive = false;
      observer?.disconnect();
    };
  }, [photoKey, count, item, tool]);
  return (
    <button
      ref={ref}
      type="button"
      className={frames.length ? 'st-row pv' : 'st-row'}
      data-n={count}
      /* disabled нет: у эталона (trendHTML) строка тренда никогда не выключена, а правило :disabled
         гасило её до opacity .38 всякий раз, когда список инструментов ещё не доехал. Действие
         разрешаем в момент нажатия — к этому времени инструмент обычно уже известен. */
      onClick={() => {
        const action = tool?.action ?? item.action ?? (tool?.entry ? { type: 'open_screen', entry: tool.entry } : null);
        if (action) onAction(action);
      }}
    >
      <span className="ic">
        <StudioIcon kind={text(item.icon) || text(tool?.section)} />
      </span>
      <span className="m">
        <b>
          <span className="tt">{title(item)}</span>
          {!!text(item.badge) && <i className="st-nw">{uiText(BADGES[text(item.badge)] ?? text(item.badge))}</i>}
        </b>
        <span>{text(item.caption) || text(item.text) || text(record(tool?.need).text) || text(tool?.need)}</span>
      </span>
      {frames.length ? (
        <span className="col">
          {frames.map((frame, index) => (
            <TrendFrame key={`${frame.url}|${index}`} {...frame} />
          ))}
        </span>
      ) : (
        <span className="col">
          {(ready ? photos.slice(0, 5) : Array.from({ length: Math.min(count, 5) }, () => '')).map((url, index) =>
            url ? <FadeImg key={index} src={url} alt="" /> : <i key={index} className="sk" />,
          )}
        </span>
      )}
    </button>
  );
}
